// US-09 — client-side mirror of the leave-service holiday conflict rules, used
// to FLAG existing duplicate company holidays that clash with a statutory pack
// entry (same holiday on a different date, a spelling variant, or a longer/
// shorter name for the same day). Keeps the admin list "deduplicated" in view
// even for rows that predate the server-side create guard.

const STOPWORDS = new Set(['day', 'al', 'el', 'of', 'the', 'holiday', 'public', 'eve', 'and']);
const PROXIMITY_DAYS = 14;

export function normalizeHolidayName(s: string): string {
  return (s || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // strip accents
    .toLowerCase()
    .replace(/\b(el|ul)\b/g, 'al') // Eid transliteration variants
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function significantTokens(name: string): Set<string> {
  return new Set(
    normalizeHolidayName(name)
      .split(' ')
      .filter((tk) => tk.length > 2 && !STOPWORDS.has(tk)),
  );
}

export function namesSimilar(a: string, b: string): boolean {
  const na = normalizeHolidayName(a);
  const nb = normalizeHolidayName(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const ta = significantTokens(a);
  const tb = significantTokens(b);
  if (ta.size === 0 || tb.size === 0) return false;
  for (const tk of ta) if (tb.has(tk)) return true;
  return false;
}

const dayDiff = (a: string, b: string) =>
  Math.abs((new Date(`${a}T00:00:00`).getTime() - new Date(`${b}T00:00:00`).getTime()) / 86400000);

export interface HolidayLike { id: string; name: string; date: string }

/**
 * Returns the conflicting entry from `against` for (name, date), or null.
 * Exact same-name+same-date is treated as the same row, not a conflict.
 */
export function findConflict(name: string, date: string, against: HolidayLike[]): HolidayLike | null {
  const norm = normalizeHolidayName(name);
  for (const h of against) {
    const sameNorm = normalizeHolidayName(h.name) === norm;
    if (sameNorm && h.date === date) continue; // identical → not a conflict here
    if (sameNorm) return h; // same holiday, different date
    if (dayDiff(h.date, date) <= PROXIMITY_DAYS && namesSimilar(name, h.name)) return h;
  }
  return null;
}
